import { ToolDefinition, formatObj } from '@justrelate/chatbot';
import { Binary, Obj, load } from 'scrivito';
import { z } from 'zod';

type BasicWidget = {
  get: (name: string, type: 'string') => string;
};

type ObjUnavailable = object;

type BasicObj = {
  get: {
    (name: string, type: 'binary'): Binary | null;
    (name: string, type: 'float'): number | null;
    (name: string, type: 'referencelist'): (BasicObj | ObjUnavailable)[];
    (name: string, type: 'string'): string;
    (name: string, type: 'stringlist'): string[];
    (name: string, type: 'widgetlist'): BasicWidget[];
  };
  id: () => string;
  objData: { get: () => Record<string, unknown> | undefined };
};

const HTML_TAGS_AND_ENTITIES = /<[a-z/]+>|&[a-z]+;/g;

function documentNamesVisible(): boolean {
  if (typeof window === 'undefined') return false;

  const { hostname } = window.location;
  return hostname === 'localhost' || hostname.endsWith('bot-academy.pages.dev');
}

export class BotConfigResolver {
  private botObj: BasicObj;

  constructor(botObj: Obj) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore private API
    this.botObj = botObj._scrivitoPrivateContent;
  }

  systemPrompt(): string {
    const promptContent = this.botObj
      .get('prompt', 'widgetlist')
      .map((widget) =>
        widget.get('text', 'string').replace(HTML_TAGS_AND_ENTITIES, ''),
      )
      .filter(Boolean)
      .join('\n');

    const documentList = this.resourcesMetadata()
      .map(
        (resource, idx) =>
          `[${idx + 1}] ${resource.title}\n${resource.context}\n`,
      )
      .join('\n');

    const readToolGuidance = `## Document Access Rules

When answering questions, use the read tool to access relevant documents from the list above. Follow these rules:

1. **NEVER re-read a document you have already read in this conversation.** Once read, the full content remains in your context and memory. Simply recall the information directly.

2. If you have already read document [N], do NOT call the read tool with that document number again. Reference the content you already have.

3. Before using the read tool, check which documents you have already accessed in this conversation.

Base your answers ONLY on the content of documents you have read. If you need information from a document you haven't read yet, use the read tool to access it first.`;

    return `${promptContent}\n\nDocument List:\n\n${documentList}\n---\n\n${readToolGuidance}`;
  }

  greeting(): string {
    return this.botObj.get('greeting', 'string');
  }

  temperature(): number {
    return this.botObj.get('temperature', 'float') ?? 1;
  }

  resourcesMetadata(): {
    title: string;
    context: string;
  }[] {
    return this.availableResources().map((obj) => ({
      title: getResourceTitle(obj),
      context: getResourceContext(obj),
    }));
  }

  resourcesReadTool(): ToolDefinition<unknown> {
    const resources = this.availableResources();

    return toUntypedTool({
      name: 'read',
      description:
        'Read documents from the knowledge base to answer user questions.',
      parameters: z.object({
        documentNumbers: z
          .array(z.number())
          .describe(`Document numbers to read (1-${resources.length}).`),
      }),
      explanation: (params) =>
        documentNamesVisible()
          ? `Reading ${params.documentNumbers
              .map((id) => getResourceTitle(resources[id - 1]))
              .join(', ')}…`
          : 'Reading knowledge base…',
      run: async (params) => {
        const objJsons = await load(() => {
          // resources from parent scope cannot be used here, as they are not yet loaded on first invocation
          const availableResources = this.availableResources();
          const objs = params.documentNumbers.map(
            (id) => availableResources[id - 1],
          );

          return objs.map((obj) => {
            const objJson = obj?.objData.get();

            if (!objJson) {
              throw new Error(`Object ${obj.id()} could not be loaded`);
            }

            return objJson;
          });
        });

        const content = await Promise.all(objJsons.map(formatObj));

        // eslint-disable-next-line no-console
        console.log('bot read', content);

        return content;
      },
    });
  }

  private availableResources(): BasicObj[] {
    return this.botObj.get('resources', 'referencelist').filter(isBasicObj);
  }
}

function getResourceTitle(obj: BasicObj): string {
  const title = obj.get('title', 'string');
  if (title) return title;

  const blob = obj.get('blob', 'binary');
  if (blob instanceof Binary) return blob.original().filename();

  return obj.id();
}

function getResourceContext(obj: BasicObj): string {
  const tags = obj.get('tags', 'stringlist') || [];

  const description =
    obj.get('description', 'string') ||
    obj.get('metaDataDescription', 'string');

  let context = '';
  if (tags.length) context += `${tags.join(', ')}\n`;
  if (description) context += `${description}\n`;

  const blob = obj.get('blob', 'binary');

  if (blob instanceof Binary) {
    const text = blob.raw().metadata().get('text');
    if (typeof text === 'string') context += `${text.slice(0, 300)}\n`;
  }

  return context;
}

function toUntypedTool<T>(fn: ToolDefinition<T>): ToolDefinition<unknown> {
  return fn as ToolDefinition<unknown>;
}

function isBasicObj(obj: BasicObj | ObjUnavailable): obj is BasicObj {
  return !!(obj as BasicObj).get;
}
